import { Reducer, useEffect, useReducer, useState } from 'react';
import { LoaderFunctionArgs, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import Loading from '../Loading';
import { useApp } from '../common/AppProvider';
import { translations } from '../translations/translations';
import Action, { getValidationMessage as getActionValidationMessage } from './Action';
import AddTaskItem from './AddTaskItem';
import TaskEditorHeader from './TaskEditorHeader';
import TaskEditorFooter from './TaskEditorFooter';
import Trigger, { getValidationMessage as getTriggerValidationMessage } from './Trigger';
import {
    AutomationTaskEdit,
    AutomationTaskStatus,
    Option,
    OptionValueType,
    Action as TypeAction,
    Trigger as TypeTrigger,
} from './types';
import { isTaskAuthenticated } from './utils';

import './task.css';

export const loader = ({ params }: LoaderFunctionArgs) => params;

export type TaskEditAction =
    | { type: 'setTask'; payload: { task: AutomationTaskEdit } }
    | { type: 'updateName'; payload: { displayName: string } }
    | { type: 'addTrigger'; payload: { trigger: TypeTrigger } }
    | { type: 'updateTrigger'; payload: { optionId: string; optionValue: OptionValueType } }
    | { type: 'deleteTrigger'; payload: null }
    | { type: 'addAction'; payload: { action: TypeAction; index: number } }
    | {
          type: 'updateAction';
          payload: { index: number; optionId: string; optionValue: OptionValueType };
      }
    | { type: 'deleteAction'; payload: { index: number } };

const taskReducer = (
    task: AutomationTaskEdit | null,
    action: TaskEditAction,
): AutomationTaskEdit | null => {
    const { type, payload } = action;

    if (type === 'setTask') {
        return payload.task;
    }

    if (task === null) {
        throw 'task has not been initialized, only `setTask` can be called';
    }

    switch (type) {
        case 'updateName':
            return { ...task, displayName: payload.displayName };
        case 'addTrigger':
            return { ...task, trigger: payload.trigger };
        case 'updateTrigger': {
            const { optionId, optionValue } = payload;
            if (!task.trigger) return task;
            return {
                ...task,
                trigger: {
                    ...task.trigger,
                    configurations: {
                        ...task.trigger.configurations,
                        [optionId]: optionValue,
                    },
                },
                status: AutomationTaskStatus.ModifiedTrigger,
            };
        }
        case 'deleteTrigger':
            return { ...task, trigger: null };
        case 'addAction': {
            if (payload.index === undefined) {
                return task;
            }
            const newActions = [
                ...task.actions.slice(0, payload.index),
                payload.action,
                ...task.actions.slice(payload.index),
            ];
            return { ...task, actions: newActions };
        }
        case 'updateAction': {
            const { index, optionId, optionValue } = payload;

            const updatedAction = {
                ...task.actions[index],
                configurations: {
                    ...task.actions[index].configurations,
                    [optionId]: optionValue,
                },
            };

            const updatedActions = task.actions.map((action, indexToCheck) => {
                if (index === indexToCheck) {
                    return updatedAction;
                }
                return action;
            });

            return { ...task, actions: updatedActions };
        }
        case 'deleteAction': {
            const updatedActions = task.actions.filter((_, index) => index !== payload.index);
            return { ...task, actions: updatedActions };
        }
    }
};

const Task = () => {
    const [isSubmitted, setIsSubmitted] = useState(false);
    const { id } = useParams();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const {
        tasks,
        syncAndUpdate,
        connectors,
        language,
        isLoading,
        alertSuccess,
        alertError,
        testTask,
        isOnline,
    } = useApp();
    const [isTesting, setIsTesting] = useState(false);

    const appContainerElement = document.querySelector('footer.automate-footer .content-wrapper');

    const [task, dispatchTask] = useReducer<Reducer<AutomationTaskEdit | null, TaskEditAction>>(
        taskReducer,
        null,
    );

    // Load task from saved tasks list
    useEffect(() => {
        const task = tasks.find((t) => t.id === id);
        if (task) {
            task.status = AutomationTaskStatus.Modified;
            dispatchTask({
                type: 'setTask',
                payload: { task },
            });
        }
    }, [id, tasks]);

    // Create new task from query params
    useEffect(() => {
        if (task || Object.keys(connectors).length === 0) {
            return;
        }

        const triggerConnector = searchParams.get('trigger-connector');
        const triggerEvent = searchParams.get('trigger-event');
        const actionConnector = searchParams.get('action-connector');
        const actionEvent = searchParams.get('action-event');

        const trigger =
            triggerConnector && triggerEvent
                ? connectors[triggerConnector]?.triggers[triggerEvent]
                : null;
        const action =
            actionConnector && actionEvent
                ? connectors[actionConnector]?.actions[actionEvent]
                : null;
        if (trigger && action) {
            dispatchTask({
                type: 'setTask',
                payload: {
                    task: {
                        displayName: `${trigger.name} -> ${action.name}`,
                        trigger,
                        actions: [action],
                        status: AutomationTaskStatus.Modified,
                        isActive: true,
                        test: false,
                        createdAt: new Date().toISOString(),
                        updatedAt: new Date().toISOString(),
                    },
                },
            });
        } else if (!id) {
            // Fall back to empty task
            dispatchTask({
                type: 'setTask',
                payload: {
                    task: {
                        displayName: '',
                        trigger: null,
                        actions: [],
                        status: AutomationTaskStatus.Modified,
                        isActive: true,
                        test: false,
                        createdAt: new Date().toISOString(),
                        updatedAt: new Date().toISOString(),
                    },
                },
            });
        }
    }, [connectors, searchParams, task, id]);

    if (!task || !appContainerElement) {
        return null;
    }

    const areOptionsValid = (options: Option[], values: Record<string, OptionValueType>) => {
        return !options.some((option) => option.isRequired && !values[option.id]);
    };

    const isValid = () => {
        if (!task.trigger) {
            return false;
        }

        if (task.actions.length === 0) {
            return false;
        }

        if (!isTaskAuthenticated(task, connectors)) {
            return false;
        }

        if (!task.displayName) {
            return false;
        }

        if (!areOptionsValid(task.trigger.availableConfigurations, task.trigger.configurations)) {
            return false;
        }

        if (getTriggerValidationMessage(task.trigger)) {
            return false;
        }

        for (const action of task.actions) {
            if (!areOptionsValid(action.availableConfigurations, action.configurations)) {
                return false;
            }

            if (getActionValidationMessage(action)) {
                return false;
            }
        }

        return true;
    };

    const onSave = async () => {
        setIsSubmitted(true);

        if (!isValid()) {
            return;
        }

        const response = await syncAndUpdate([{ ...task, updatedAt: new Date().toISOString() }]);

        if (!response) {
            alertError({
                message: translations[language].ERROR_unknown,
            });
            return;
        }

        const isNewTask = !id;
        if (response.taskErrors.length > 0) {
            alertError({
                message: isNewTask
                    ? translations[language].TASK_create_with_errors
                    : translations[language].TASK_save_with_errors,
            });
        } else {
            alertSuccess({
                message: isNewTask
                    ? translations[language].TASK_create_success_message
                    : translations[language].TASK_save_success_message,
            });
        }

        navigate('/tasks');
    };

    const onTest = async () => {
        setIsSubmitted(true);

        if (isValid()) {
            setIsTesting(true);

            try {
                await testTask(task, {});
                alertSuccess({
                    message: translations[language].TASK_test_success_message,
                });
            } catch (e) {
                alertError({
                    message: (e as Error).message,
                });
            } finally {
                setIsTesting(false);
            }
        }
    };

    const onUpdateName = (taskName: string) => {
        dispatchTask({
            type: 'updateName',
            payload: { displayName: taskName },
        });
    };

    const onToggle = async (task: AutomationTaskEdit) => {
        if (!task) {
            alertError({
                message: translations[language].ERROR_unknown,
            });
            return;
        }

        const response = await syncAndUpdate([
            {
                ...task,
                isActive: !task.isActive,
                status: AutomationTaskStatus.Modified,
                updatedAt: new Date().toISOString(),
            },
        ]);

        if (response && response.taskErrors.length > 0) {
            alertError({
                message: translations[language].TASK_enable_failed,
            });
        }
    };

    const showValidation =
        isSubmitted &&
        (!task.displayName ||
            !task.trigger ||
            task.actions.length === 0 ||
            !isTaskAuthenticated(task, connectors));

    return (
        <>
            <main className="automate-content task-editor-layout">
                <TaskEditorHeader
                    taskId={id}
                    taskName={task.displayName}
                    isInvalidTaskName={isSubmitted && !task.displayName}
                    isTaskActive={task.isActive}
                    isToggleDisabled={!isTaskAuthenticated(task, connectors) || !isOnline}
                    onUpdateName={onUpdateName}
                    onToggle={() => onToggle(task)}
                    onTest={onTest}
                    onSave={onSave}
                />

                <section className="task-editor-content">
                    {showValidation ? (
                        <section className="task-validation-errors">
                            {!task.displayName && (
                                <p>{translations[language].TASK_validation_name}</p>
                            )}
                            {!task.trigger && task.actions.length !== 0 && (
                                <p>{translations[language].TASK_validation_trigger}</p>
                            )}
                            {task.trigger && task.actions.length === 0 && (
                                <p>{translations[language].TASK_validation_actions}</p>
                            )}
                            {!task.trigger && task.actions.length == 0 && (
                                <p>{translations[language].TASK_validation_trigger_and_actions}</p>
                            )}
                            {!isTaskAuthenticated(task, connectors) && (
                                <p>{translations[language].TASK_validation_authentication}</p>
                            )}
                        </section>
                    ) : null}

                    <section className="task-item-trigger-section">
                        <h3 className="text lg">
                            {translations[language].TASK_trigger_section_title}
                        </h3>
                    </section>
                    {task.trigger ? (
                        <Trigger
                            trigger={task.trigger}
                            dispatchTask={dispatchTask}
                            showValidation={isSubmitted}
                        />
                    ) : (
                        <AddTaskItem itemType="trigger" dispatchTask={dispatchTask} />
                    )}
                    <section className="task-item-action-section">
                        <h3 className="text lg">
                            {translations[language].TASK_actions_section_title}
                        </h3>
                    </section>
                    {task.actions.map((action, index) => [
                        <AddTaskItem
                            key={'add-action-' + index + task.actions.length}
                            itemType="action"
                            index={index}
                            dispatchTask={dispatchTask}
                        />,
                        <Action
                            trigger={task.trigger}
                            key={'action-' + index + task.actions.length}
                            action={action}
                            dispatchTask={dispatchTask}
                            index={index}
                            showValidation={isSubmitted}
                        />,
                    ])}
                    <AddTaskItem
                        itemType="action"
                        index={task.actions.length}
                        dispatchTask={dispatchTask}
                    />
                </section>
                <TaskEditorFooter onSave={onSave} onTest={onTest} taskId={id} />
            </main>
            {isTesting || isLoading ? (
                <Loading message={translations[language].COMMON_loading} />
            ) : null}
        </>
    );
};

export default Task;
